import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Wallet, FileText, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import OrdersTable from "./OrdersTable";

const fmt = (v) => `R$ ${(v || 0).toFixed(2).replace(".", ",")}`;

export default function MerchantReports() {
  const [redemptions, setRedemptions] = useState(null);
  const [recharges, setRecharges] = useState(null);

  const load = async () => {
    try {
      const [r, c] = await Promise.all([
        api.get("/merchant/reports/redemptions"),
        api.get("/merchant/reports/recharges"),
      ]);
      setRedemptions(r.data);
      setRecharges(c.data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => { load(); }, []);

  return (
    <Tabs defaultValue="payables" className="mt-2">
      <TabsList className="bg-stone-100 rounded-xl">
        <TabsTrigger value="payables" data-testid="m-tab-payables"><Wallet size={14} className="mr-1"/>Saldo a Receber</TabsTrigger>
        <TabsTrigger value="recharges" data-testid="m-tab-recharges"><FileText size={14} className="mr-1"/>Cargas de Saldo</TabsTrigger>
        <TabsTrigger value="orders" data-testid="m-tab-orders"><ShoppingBag size={14} className="mr-1"/>Vendas Checkout</TabsTrigger>
      </TabsList>

      <TabsContent value="payables" className="mt-5 space-y-4">
        {redemptions && (<>
          <div className="grid sm:grid-cols-3 gap-3">
            <Card className="p-4 rounded-2xl bg-emerald-50 border-emerald-200"><div className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">A receber</div><div className="font-display text-2xl text-emerald-900 mt-1">{fmt(redemptions.totals.to_receive)}</div></Card>
            <Card className="p-4 rounded-2xl bg-blue-50 border-blue-200"><div className="text-[10px] uppercase tracking-wider text-blue-900 font-bold">Já recebido</div><div className="font-display text-2xl text-blue-950 mt-1">{fmt(redemptions.totals.already_paid)}</div></Card>
            <Card className="p-4 rounded-2xl bg-stone-100 border-stone-200"><div className="text-[10px] uppercase tracking-wider text-stone-700 font-bold">Resgates totais</div><div className="font-display text-2xl text-stone-900 mt-1">{redemptions.totals.count}</div></Card>
          </div>

          <Card className="p-5 rounded-2xl border-stone-200">
            <h3 className="font-display text-lg font-semibold mb-3">Resgates realizados (cashback usado pelos clientes)</h3>
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-stone-500 text-[11px] uppercase border-b border-stone-200">
                  <th className="px-2 py-2">Data</th><th>Empresa</th><th className="text-right">Valor a receber</th><th className="text-right">Taxa retida</th><th>Status</th>
                </tr></thead>
                <tbody className="divide-y divide-stone-100">
                  {redemptions.redemptions.length === 0 && <tr><td colSpan="5" className="px-2 py-6 text-center text-stone-500">Nenhum resgate realizado ainda.</td></tr>}
                  {redemptions.redemptions.map((p) => (
                    <tr key={p.id} data-testid={`m-payout-${p.id}`}>
                      <td className="px-2 py-2 text-[11px] text-stone-500">{new Date(p.created_at).toLocaleString("pt-BR")}</td>
                      <td>{p.company_name}</td>
                      <td className="text-right font-bold text-emerald-700">{fmt(p.amount)}</td>
                      <td className="text-right text-stone-600">{fmt(p.admin_fee)}</td>
                      <td><span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${p.status === "paid" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`}>{p.status === "paid" ? "Pago" : "Pendente"}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>)}
      </TabsContent>

      <TabsContent value="recharges" className="mt-5 space-y-4">
        {recharges && (<>
          <div className="grid sm:grid-cols-3 gap-3">
            <Card className="p-4 rounded-2xl bg-emerald-50 border-emerald-200"><div className="text-[10px] uppercase tracking-wider text-emerald-800 font-bold">Confirmadas</div><div className="font-display text-2xl text-emerald-900 mt-1">{fmt(recharges.totals.total_confirmed)}</div></Card>
            <Card className="p-4 rounded-2xl bg-amber-50 border-amber-200"><div className="text-[10px] uppercase tracking-wider text-amber-800 font-bold">Pendentes</div><div className="font-display text-2xl text-amber-900 mt-1">{fmt(recharges.totals.total_pending)}</div></Card>
            <Card className="p-4 rounded-2xl bg-stone-100 border-stone-200"><div className="text-[10px] uppercase tracking-wider text-stone-700 font-bold">Total</div><div className="font-display text-2xl text-stone-900 mt-1">{recharges.totals.count}</div></Card>
          </div>

          <Card className="p-5 rounded-2xl border-stone-200">
            <h3 className="font-display text-lg font-semibold mb-3">Histórico de cargas</h3>
            <div className="overflow-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-stone-500 text-[11px] uppercase border-b border-stone-200">
                  <th className="px-2 py-2">Data</th><th>Empresa</th><th className="text-right">Valor</th><th>Status</th>
                </tr></thead>
                <tbody className="divide-y divide-stone-100">
                  {recharges.recharges.length === 0 && <tr><td colSpan="4" className="px-2 py-6 text-center text-stone-500">Nenhuma recarga ainda.</td></tr>}
                  {recharges.recharges.map((r) => (
                    <tr key={r.id}>
                      <td className="px-2 py-2 text-[11px] text-stone-500">{new Date(r.created_at).toLocaleString("pt-BR")}</td>
                      <td>{r.company_name}</td>
                      <td className="text-right font-bold">{fmt(r.amount)}</td>
                      <td><span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase ${r.status === "confirmed" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-900"}`}>{r.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>)}
      </TabsContent>

      <TabsContent value="orders" className="mt-5">
        <OrdersTable scope="merchant"/>
      </TabsContent>
    </Tabs>
  );
}
